// "How we work" - 3-step process (formerly TwelveWeekRoadmap)
import React from 'react';
import { MessageSquare, FileText, Rocket, Zap } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface Phase {
  step: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const phases: Phase[] = [
  {
    step: 'Step 1',
    title: 'Discovery call',
    description:
      "We spend 30 minutes understanding the problem you're trying to solve. What you have, what's broken, and what a good result actually looks like for your business. Free. No pitch. No commitment.",
    icon: <MessageSquare className="w-6 h-6" />,
    color: 'from-cyan-500 to-blue-500',
  },
  {
    step: 'Step 2',
    title: 'Proposal and prototype',
    description:
      "We send you a clear proposal. What we'll build, how it works, what it costs, and how long it takes. For bigger projects, we build a working prototype first so you can see what you're getting before you commit.",
    icon: <FileText className="w-6 h-6" />,
    color: 'from-blue-500 to-violet-500',
  },
  {
    step: 'Step 3',
    title: 'Build and launch',
    description:
      "We build it, test it, and get it live. You're involved at every step, not locked out until delivery day. After launch, we're available for updates, new features, and anything else you need as the tool grows with your business.",
    icon: <Rocket className="w-6 h-6" />,
    color: 'from-violet-500 to-primary',
  },
];

const PhaseCard = ({ phase, index, isVisible }: { phase: Phase; index: number; isVisible: boolean }) => {
  const stepNumber = String(index + 1).padStart(2, '0');
  return (
    <div
      className="relative flex flex-col"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
        transition: `all 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${index * 80}ms`,
      }}
    >
      <div className="group relative flex flex-col h-full rounded-2xl border border-white/[0.07] bg-[#0f0f0f] p-7 lg:p-8 transition-all duration-300 hover:border-primary/30 hover:-translate-y-0.5">
        <div className="flex items-baseline justify-between mb-6">
          <span className="text-[11px] font-mono font-semibold text-white/70 tracking-[0.2em]">
            {stepNumber}
          </span>
          <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-primary group-hover:border-primary/30 transition-colors duration-300">
            {phase.icon}
          </div>
        </div>

        <h3 className="text-xl font-semibold text-white mb-3 tracking-[-0.015em]">{phase.title}</h3>
        <div className="h-px w-10 bg-primary/40 mb-4" />
        <p className="text-[15px] text-white/75 leading-relaxed">
          {phase.description}
        </p>
      </div>
    </div>
  );
};

export const TwelveWeekRoadmap = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });

  return (
    <section
      id="how-it-works"
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a]"
    >
      <div className="container-professional relative z-10">
        <div
          className="max-w-2xl mb-16 lg:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold text-primary uppercase tracking-[0.2em] mb-5">
            <Zap className="w-3.5 h-3.5" strokeWidth={2.25} />
            How it works
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-bold text-white mb-4 tracking-[-0.025em] leading-[1.1]">
            How we work
          </h2>
          <p className="text-base md:text-lg text-white/55 leading-relaxed">
            Straightforward. No twelve step consulting process.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {phases.map((phase, index) => (
            <PhaseCard
              key={phase.title}
              phase={phase}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
